export const ONE_TRUST_GROUPS = {
  STRICTLY_NECESSARY: 'C0001',
  ANALYTICS: 'C0002',
  FUNCTIONAL: 'C0003',
  TARGETING: 'C0004',
} as const;

export const ONETRUST_CONSENT_CHANGED_EVENT = 'onetrust-consent-changed';

function getOptanonConsentValue(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)OptanonConsent=([^;]*)/);
  if (!match) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

function getInteractionCount(): number {
  const consentValue = getOptanonConsentValue();
  if (!consentValue) return 0;

  const match = consentValue.match(/(?:^|&)interactionCount=(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * OneTrust pre-writes OptanonConsent with default groups (e.g. C0004:1) at
 * interactionCount=0 before the user clicks anything. Real consent requires
 * both the banner to be dismissed and interactionCount > 0.
 */
export function hasUserMadeConsentChoice(): boolean {
  return (
    document.cookie.includes('OptanonAlertBoxClosed=') &&
    getInteractionCount() > 0
  );
}

/**
 * OneTrust sets OnetrustActiveGroups to default/opt-in categories before the user
 * confirms. The OptanonConsent cookie groups= param is the real source of truth
 * after the banner has been dismissed.
 */
function getConsentedGroupsFromCookie(): Set<string> {
  if (!hasUserMadeConsentChoice()) return new Set();

  const consentValue = getOptanonConsentValue();
  if (!consentValue) return new Set();

  const groupsMatch = consentValue.match(/groups=([^&]*)/);
  if (!groupsMatch) return new Set();

  const consented = new Set<string>();
  groupsMatch[1].split(',').forEach((entry) => {
    const [id, status] = entry.split(':');
    if (id && status === '1') consented.add(id.trim());
  });
  return consented;
}

export function hasConsent(category: string): boolean {
  if (!hasUserMadeConsentChoice()) return false;
  return getConsentedGroupsFromCookie().has(category);
}

export function hasAnalyticsConsent(): boolean {
  return hasConsent(ONE_TRUST_GROUPS.ANALYTICS);
}

export function hasTargetingConsent(): boolean {
  return hasConsent(ONE_TRUST_GROUPS.TARGETING);
}

export function subscribeToConsentChanges(callback: () => void): () => void {
  const handler = () => callback();
  window.addEventListener(ONETRUST_CONSENT_CHANGED_EVENT, handler);
  return () =>
    window.removeEventListener(ONETRUST_CONSENT_CHANGED_EVENT, handler);
}
